/** Internal users (aa_app_rl_role_id === 2) may be assigned for any application. */
export function isInternalRoleEmployee(employee) {
  if (!employee || employee.roleId === undefined || employee.roleId === null) {
    return false;
  }
  const n = Number(employee.roleId);
  return !Number.isNaN(n) && n === 2;
}

/** External users (aa_app_rl_role_id === 3): only eligible when their default application list matches. */
export function isExternalRoleEmployee(employee) {
  if (!employee || employee.roleId === undefined || employee.roleId === null) {
    return false;
  }
  const n = Number(employee.roleId);
  return !Number.isNaN(n) && n === 3;
}

/** Parse aa_app_rl_default_application_id (may be comma-separated) into a Set of id strings. */
export function parseEmployeeApplicationIdSet(defaultAppRaw) {
  if (defaultAppRaw == null || defaultAppRaw === "") return new Set();
  return new Set(
    String(defaultAppRaw)
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s !== "" && s.toLowerCase() !== "null")
  );
}

/**
 * True when the employee's comma-separated default application ids include selectedApplicationId.
 * Used for external users and any non-internal user without the global-application exception.
 */
export function defaultApplicationIdsInclude(defaultAppRaw, selectedApplicationId) {
  const id = String(selectedApplicationId).trim();
  if (!id) return false;
  const set = parseEmployeeApplicationIdSet(defaultAppRaw);
  return set.has(id);
}

/**
 * Whether this person should appear for the given application in task pickers / validation.
 *
 * - No application selected: show everyone (caller may still apply search).
 * - Internal (role 2): always eligible for every application.
 * - External (role 3): eligible only if aa_app_rl_default_application_id lists that application
 *   (if it does not match, they must not appear in lists and selections are invalid).
 * - Any other role: same rule as external — must match default application ids.
 */
export function employeeHasAccessToApplication(employee, selectedApplicationId) {
  if (!selectedApplicationId || String(selectedApplicationId).trim() === "") {
    return true;
  }
  if (isInternalRoleEmployee(employee)) {
    return true;
  }
  // External (3) and others: strict match on profile application list only
  return defaultApplicationIdsInclude(employee.defaultApplicationIds, selectedApplicationId);
}

export function getApplicationMismatchMessages(
  selectedApplicationId,
  selectedOwner,
  selectedSecondaryOwners,
  selectedEmployees
) {
  if (!selectedApplicationId || String(selectedApplicationId).trim() === "") return [];
  const messages = [];
  if (selectedOwner && !employeeHasAccessToApplication(selectedOwner, selectedApplicationId)) {
    messages.push(`Task owner "${selectedOwner.name}" is not applicable for this application.`);
  }
  (selectedSecondaryOwners || []).forEach((emp) => {
    if (!employeeHasAccessToApplication(emp, selectedApplicationId)) {
      messages.push(`Secondary owner "${emp.name}" is not applicable for this application.`);
    }
  });
  (selectedEmployees || []).forEach((emp) => {
    if (!employeeHasAccessToApplication(emp, selectedApplicationId)) {
      messages.push(`"${emp.name}" (Assign to) is not applicable for this application.`);
    }
  });
  return messages;
}

export function getInvalidEmployeeFlagsForApplication(
  selectedApplicationId,
  selectedOwner,
  selectedSecondaryOwners,
  selectedEmployees
) {
  if (!selectedApplicationId || String(selectedApplicationId).trim() === "") {
    return { ownerInvalid: false, secondaryInvalidIds: new Set(), assignedInvalidIds: new Set() };
  }
  const secondaryInvalidIds = new Set();
  const assignedInvalidIds = new Set();
  let ownerInvalid = false;
  if (selectedOwner && !employeeHasAccessToApplication(selectedOwner, selectedApplicationId)) {
    ownerInvalid = true;
  }
  (selectedSecondaryOwners || []).forEach((emp) => {
    if (!employeeHasAccessToApplication(emp, selectedApplicationId)) {
      secondaryInvalidIds.add(emp.id);
    }
  });
  (selectedEmployees || []).forEach((emp) => {
    if (!employeeHasAccessToApplication(emp, selectedApplicationId)) {
      assignedInvalidIds.add(emp.id);
    }
  });
  return { ownerInvalid, secondaryInvalidIds, assignedInvalidIds };
}
